import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useLocation, useParams } from 'react-router-dom';
import { useUserContext } from '../context/UserContext';
import { LIST_TRANSACTIONS } from '../apollo/queries/orders';
import { formatName, formatString } from '../utils';

const useOrderFilters = (limit, filters = {}) => {
  const { userData } = useUserContext();
  const {
    customerName = '',
    dateRange = { start: null, end: null },
    orderStatus = [],
    priceRange = { min: '', max: '' },
    searchQuery = '',
  } = filters;
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const params = useParams();
  const { data, loading } = useQuery(gql(LIST_TRANSACTIONS), {
    variables: {
      brandName: userData?.brand !== 'saddlefitadmin' && (userData?.type === 'Admin' || userData?.type === 'Member') ? userData?.brand : params.brandSlug,
    },
    fetchPolicy: 'network-only',
  });

  let orders = data?.listTransactions?.items || [];

  orders = orders
    ?.filter((item) => {
      if (!customerName) return true;
      return item.orderId?.toLowerCase().includes(customerName.toLowerCase()) ||
        item.buyerId?.toLowerCase().includes(customerName.toLowerCase());
    })
    ?.filter((item) => {
      const itemDate = dayjs(item.createdAt || item.shopify?.orderDate);

      const startDate = dateRange.start ? dayjs(dateRange.start).startOf('day') : null;
      const endDate = dateRange.end ? dayjs(dateRange.end).endOf('day') : null;

      if (startDate && endDate) {
        return itemDate.isSameOrAfter(startDate) && itemDate.isSameOrBefore(endDate);
      }

      if (startDate && !endDate) {
        return itemDate.isSameOrAfter(startDate);
      }

      if (!startDate && endDate) {
        return itemDate.isSameOrBefore(endDate);
      }

      return true;
    })
    ?.filter((item) => {
      if (!orderStatus.length) return true;
      const selectedStatuses = orderStatus.map((s) => s.value);

      // Determine the latest order status based on timestamp (desc), fallback to 'N/A'
      const latestOrderStatus = (item?.orderStatus?.length
        ? [...item.orderStatus]
          .sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))
          .map((s) => s?.status)[0]
        : 'N/A');

      // Match only against the selected Order Status values
      return selectedStatuses.includes(latestOrderStatus);
    })
    ?.filter((item) => {
      const total = parseFloat(item.totalPrice?.replace(/[$,]/g, '') || 0);
      const minPrice = priceRange?.min ? parseFloat(priceRange.min) : null;
      const maxPrice = priceRange?.max ? parseFloat(priceRange.max) : null;
      return (
        (minPrice === null || total >= minPrice) &&
        (maxPrice === null || total <= maxPrice)
      );
    })
    ?.filter((item) => {
      if (!searchQuery) return true;
      const query = searchQuery?.toLowerCase();
      return Object.values(item)?.some((value) =>
        value?.toString()?.toLowerCase().includes(query)
      );
    });

  const totalItems = orders?.length;
  const totalPages = Math?.ceil(totalItems / limit);

  useEffect(() => {
    setCurrentPage(1);
  }, [totalItems, limit, customerName, dateRange.start, dateRange.end, orderStatus, priceRange.min, priceRange.max]);

  const paginatedOrders = orders.slice((currentPage - 1) * limit, currentPage * limit);

  return {
    state: {
      orders: paginatedOrders,
      loading,
      currentPage,
      totalPages,
      canLoadNext: currentPage < totalPages,
      canLoadPrevious: currentPage > 1,
    },
    actions: {
      loadNextPage: () => setCurrentPage((p) => Math.min(p + 1, totalPages)),
      loadPreviousPage: () => setCurrentPage((p) => Math.max(p - 1, 1)),
    },
  };
};

export default useOrderFilters;